import React, { useState, useEffect, useCallback, useMemo } from "react"
import { GetUserLicenses, GetLicenseDetails } from "aldoo-ra/Admin/ProductLicensing/api"
import useSigninController from "aldoo-ra/Signin/signin-controller"
import { usePageContext } from "vike-react/usePageContext"
import { navigate } from "vike/client/router"
import LicenseActivation from "./LicenseActivation"
import LicenseInfo from "./LicenseInfo"
import LicenseCard from "./LicenseCard"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import { EyeIcon, ChevronRightIcon, HomeIcon, ChevronLeftIcon, PlusCircleIcon } from "@heroicons/react/24/outline"
import Breadcrumbs from "aldoo-ra/Breadcrumbs"
import { url } from "aldoo-ra/CMS/url"

const Activate = ({ activationMode = false }) => {
  const [userLicenses, setUserLicenses] = useState([])
  const [selectedLicenseId, setSelectedLicenseId] = useState(null)
  const [selectedLicenseDetails, setSelectedLicenseDetails] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showActivation, setShowActivation] = useState(activationMode)
  const [refreshKey, setRefreshKey] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const licensesPerPage = 5
  const { userProfile } = useSigninController()
  const { t } = Localization()
  const texts = TextBundle({ id: "licenses_main" })
  const pageContext = usePageContext()

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Parse URL parameters
  const getUrlParams = useCallback(() => {
    if (typeof window === 'undefined') return { licenseId: null, tab: null }
    const urlParams = new URLSearchParams(window.location.search)
    return {
      licenseId: urlParams.get('license'),
      tab: urlParams.get('tab')
    }
  }, [])

  // Update URL parameters
  const updateUrlParams = useCallback((licenseId, tab) => {
    if (typeof window === 'undefined') return
    
    const url = new URL(window.location)
    if (licenseId) {
      url.searchParams.set('license', licenseId)
    } else {
      url.searchParams.delete('license')
    }
    
    if (tab) {
      url.searchParams.set('tab', tab)
    } else {
      url.searchParams.delete('tab')
    }
    
    window.history.pushState(null, '', url.toString())
  }, [])

  // DataTable-compatible API method for fetching licenses  
  const getUserLicensesForTable = useCallback(async () => {
    try {
      const response = await GetUserLicenses()
      
      if (response.result && response.data && Array.isArray(response.data)) {
        // Filter out external licenses - only show internal licenses
        const internalLicenses = response.data.filter(license => 
          !license.external && !(license.model?.external)
        )
        
        // Return data with proper pagination info for DataTable
        return { 
          result: true, 
          data: internalLicenses,
          total: internalLicenses.length,
          totalItems: internalLicenses.length
        }
      } else {
        return { 
          result: true, 
          data: [],
          total: 0,
          totalItems: 0
        }
      }
    } catch (error) {
      console.error('Error loading user licenses for table:', error)
      return { 
        result: false, 
        data: [],
        total: 0,
        totalItems: 0
      }
    }
  }, [])

  // Load user's licenses (filter out external licenses)
  const loadUserLicenses = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await GetUserLicenses()
      
      if (response.result && response.data && Array.isArray(response.data)) {
        // Filter out external licenses - only show internal licenses
        const internalLicenses = response.data.filter(license => 
          !license.external && !(license.model?.external)
        )
        setUserLicenses(internalLicenses)
      } else {
        setUserLicenses([])
      }
    } catch (error) {
      console.error('Error loading user licenses:', error)
      setUserLicenses([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Load detailed license information when one is selected
  const loadSelectedLicenseDetails = useCallback(async (licenseId) => {
    try {
      const detailsResponse = await GetLicenseDetails(licenseId)
      if (detailsResponse.result && detailsResponse.data) {
        const basicLicense = userLicenses.find(license => license._id === licenseId)
        setSelectedLicenseDetails({
          ...basicLicense,
          ...detailsResponse.data
        })
      }
    } catch (error) {
      console.error('Error loading license details:', error)
      setSelectedLicenseDetails(null)
    }
  }, [userLicenses])

  // Load licenses on component mount
  useEffect(() => {
    if (userProfile) {
      loadUserLicenses()
    }
  }, [userProfile, loadUserLicenses])

  // Update showActivation when activationMode prop changes
  useEffect(() => {
    if (activationMode && !showActivation) {
      setShowActivation(true)
      setSelectedLicenseId(null)
      setSelectedLicenseDetails(null)
    }
  }, [activationMode, showActivation])

  // Handle URL parameters when licenses are loaded
  useEffect(() => {
    if (userLicenses.length > 0) {
      const { licenseId } = getUrlParams()
      if (licenseId) {
        // Find license by _id
        const foundLicense = userLicenses.find(license => license._id === licenseId)
        if (foundLicense) {
          setSelectedLicenseId(licenseId)
          loadSelectedLicenseDetails(licenseId)
        } else {
          // License not found, clear URL params and show table
          updateUrlParams(null, null)
          setSelectedLicenseId(null)
          setSelectedLicenseDetails(null)
        }
      } else {
        // No license parameter, ensure we're showing the table view
        setSelectedLicenseId(null)
        setSelectedLicenseDetails(null)
      }
    }
  }, [userLicenses, loadSelectedLicenseDetails])

  // Handle browser back/forward navigation and URL changes
  useEffect(() => {
    const handlePopState = () => {
      const { licenseId } = getUrlParams()
      if (licenseId && userLicenses.length > 0) {
        const foundLicense = userLicenses.find(license => license._id === licenseId)
        if (foundLicense) {
          setSelectedLicenseId(licenseId)
          loadSelectedLicenseDetails(licenseId)
        }
      } else {
        setSelectedLicenseId(null)
        setSelectedLicenseDetails(null)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [userLicenses, loadSelectedLicenseDetails])

  // Listen for URL changes to detect menu navigation
  useEffect(() => {
    const currentPath = pageContext.urlPathname
    const currentSearch = typeof window !== 'undefined' ? window.location.search : ''
    
    // Handle /account/licenses/activate - show activation interface
    if (currentPath === '/account/licenses/activate' || activationMode) {
      setShowActivation(true)
      setSelectedLicenseId(null)
      setSelectedLicenseDetails(null)
    }
    // Handle /account/licenses
    else if (currentPath === '/account/licenses') {
      const { licenseId } = getUrlParams()
      
      // Handle license selection with parameters
      if (licenseId && userLicenses.length > 0) {
        const foundLicense = userLicenses.find(license => license._id === licenseId)
        if (foundLicense) {
          setSelectedLicenseId(licenseId)
          loadSelectedLicenseDetails(licenseId)
          setShowActivation(false)
        }
      }
      // Handle /account/licenses without parameters - show licenses table
      else if (!currentSearch || !licenseId) {
        // Force show licenses table by clearing any selected license state
        if (selectedLicenseId || selectedLicenseDetails || showActivation) {
          setSelectedLicenseId(null)
          setSelectedLicenseDetails(null)
          setShowActivation(false)
        }
      }
    }
  }, [pageContext.urlPathname, activationMode, userLicenses, selectedLicenseId, selectedLicenseDetails, showActivation, loadSelectedLicenseDetails])

  // Additional effect to monitor window URL changes (for programmatic navigation)
  useEffect(() => {
    const checkUrlAndUpdate = () => {
      const currentPath = window.location.pathname
      const currentSearch = window.location.search
      
      // Handle /account/licenses/activate
      if (currentPath === '/account/licenses/activate' || activationMode) {
        if (!showActivation) {
          setShowActivation(true)
          setSelectedLicenseId(null)
          setSelectedLicenseDetails(null)
        }
      }
      // Force table view when navigating to clean /account/licenses
      else if (currentPath === '/account/licenses' && !currentSearch) {
        // Aggressively clear state to ensure table view
        if (selectedLicenseId !== null || selectedLicenseDetails !== null || showActivation !== false) {
          setSelectedLicenseId(null)
          setSelectedLicenseDetails(null)
          setShowActivation(false)
        }
      }
    }

    // Check immediately and frequently
    checkUrlAndUpdate()

    // More frequent checking to catch programmatic navigation
    const interval = setInterval(checkUrlAndUpdate, 100)
    
    return () => clearInterval(interval)
  }, [activationMode])


  // Handle license selection
  const handleLicenseSelect = useCallback((licenseId) => {
    // Immediately update state
    setSelectedLicenseId(licenseId)
    setShowActivation(false) // Hide activation when selecting license
    if (licenseId) {
      loadSelectedLicenseDetails(licenseId)
      updateUrlParams(licenseId, 'details')
    } else {
      setSelectedLicenseDetails(null)
      updateUrlParams(null, null)
    }
  }, [loadSelectedLicenseDetails, updateUrlParams])

  // Handle successful activation - reload license data
  const handleActivationSuccess = () => {
    loadUserLicenses()
    setRefreshKey(prev => prev + 1) // Refresh DataTable
    setSelectedLicenseId(null)
    setSelectedLicenseDetails(null)
    setShowActivation(false) // Hide activation dialog
    updateUrlParams(null, null)
  }

  // Handle license updates (after user management actions)
  const handleLicenseUpdate = () => {
    loadUserLicenses()
    setRefreshKey(prev => prev + 1) // Refresh DataTable
    if (selectedLicenseId) {
      loadSelectedLicenseDetails(selectedLicenseId)
      // Keep the current URL params since we're just refreshing the same license
    }
  }

  // Helper functions
  const getStatusConfig = (status) => {
    const configs = {
      free: { 
        color: "bg-success/10 text-success", 
        label: texts?.status_free || "Free" 
      },
      active: { 
        color: "bg-primary/10 text-gray_1", 
        label: texts?.status_active || "Active" 
      },
      canceled: { 
        color: "bg-error/10 text-error", 
        label: texts?.status_canceled || "Canceled"
      },
      grace_period: { 
        color: "bg-warning/10 text-warning", 
        label: texts?.status_grace || "Grace Period" 
      },
      on_hold: { 
        color: "bg-warning/10 text-gray_1", 
        label: texts?.status_on_hold || "On Hold" 
      },
      paused: { 
        color: "bg-progress/20 text-gray_1", 
        label: texts?.status_paused || "Paused"  
      },
      expired: { 
        color: "bg-error/10 text-gray_1", 
        label: texts?.status_expired || "Expired" 
      },
      revoked: { 
        color: "bg-gray_3/10 text-gray_1", 
        label: texts?.status_revoked || "Revoked" 
      }
    }
    return configs[status?.toLowerCase()] || { color: "bg-gray_3 text-white", label: status?.charAt(0).toUpperCase() + status?.slice(1) || texts?.status_default || "Unknown" }
  }

  const calculateUsagePercentage = (usedSeats, totalSeats) => {
    if (!totalSeats || totalSeats === 0) return 0
    return Math.round((usedSeats / totalSeats) * 100)
  }


  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <Typography 
            variant="info_16" 
            className="text-text_secondary my-0"
          >
            {texts?.loading || "Loading your licenses..."}
          </Typography>
        </div>
      </div>
    )
  }

  // If showing license details (only when we have both ID and details loaded)
  if (selectedLicenseId && selectedLicenseDetails) {
    const productName = selectedLicenseDetails.productName || selectedLicenseDetails.productId || texts?.unknown_product || "Unknown Product"

    return (
      <div className="space-y-6">
        {/* Breadcrumb Navigation */}
        <Breadcrumbs
          variant="aqua_account"
          separatorIcon={ChevronRightIcon}
          items={[
            {
              label: texts?.breadcrumbs_licenses || "Licenses",
              icon: HomeIcon,
              onClick: () => navigate('/account/licenses')
            },
            {
              label: productName
            }
          ]}
        />

        {/* Use existing LicenseInfo component with detailed data */}
        <LicenseInfo 
          license={selectedLicenseDetails} 
          userProfile={userProfile}
          onLicenseUpdate={handleLicenseUpdate}
        />
      </div>
    )
  }

  // Show activation interface if no licenses OR if activation dialog is requested
  if (userLicenses.length === 0 || showActivation) {
    return (
      <div className="space-y-6">
        {/* Show back button if we have licenses but are showing activation */}
        {userLicenses.length > 0 && (
          <button
            onClick={() => {
              // Navigate back to /account/licenses using Vike navigation
              navigate('/account/licenses')
            }}
            className="flex items-center text-gray_2 hover:text-primary transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-1" />
            {texts?.back_button || "Back to Licenses"}
          </button>
        )}
        <LicenseActivation onActivationSuccess={handleActivationSuccess} />
      </div>
    )
  }

  // Main licenses table view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-center justify-between">
        <Typography 
          component="h2"
          variant="h5" 
          className="text-text"
        >
          {texts?.licenses_title || "Your Licenses"}
        </Typography>
        
        <div className="flex flex-col md:flex-row items-center gap-1 md:gap-4 mt-3 md:mt-0">
          <Button
            variant="primary_s"
            onClick={() => {
              // Navigate to /account/licenses/activate using Vike navigation
              navigate('/account/licenses/activate')
            }}
            className="flex items-center gap-2"
          >
            <PlusCircleIcon className="w-5 h-5" />
            {texts?.activate_button || "Activate License"}
          </Button>
        </div>
      </div>

      {/* Responsive Licenses Display */}
      {isMobile ? (
        /* Mobile: Card View */
        <div className="w-full flex flex-col items-center gap-4">
          {userLicenses.map((license) => (
            <LicenseCard
              key={license._id}
              license={license}
              onLicenseSelect={handleLicenseSelect}
              getStatusConfig={getStatusConfig}
              calculateUsagePercentage={calculateUsagePercentage}
            />
          ))}
        </div>
      ) : (
        /* Desktop: DataTable View */
        <div className="bg-paper rounded-[24px] shadow-custom_card overflow-hidden">
          <DataTable
            variant="browser"
            apiMethod={getUserLicensesForTable}
            refreshKey={refreshKey}
            showSearch={false}
            itemsPerPage={licensesPerPage}
            localUserPreferencesKey="account.licenses"
            onDataLoadingElement={
              <div className="flex items-center justify-center py-20">
                <div className="text-text_secondary">Loading licenses...</div>
              </div>
            }
            styleOverrides={{
              table: "w-full table-fixed",
              header: "w-full flex justify-between items-end gap-2 mt-3",
              container: "relative w-full flex flex-col h-[65vh] justify-start bg-paper px-4 py-4",
              getRowStyle: () => {
                return "bg-paper border-b border-bordered hover:bg-gray_3/10 transition-colors cursor-pointer"
              },
              onRowClick: (item) => {
                handleLicenseSelect(item._id)
              }
            }}
            renders={[
              {
                id: "product",
                title: texts?.product_column || "Product",
                headerClassName: "w-[40%] font-medium",
                render: (_, license) => {
                  const productName = license.productName || license.productId || "Unknown Product"
                  return (
                    <div className="py-2">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray_3/30 rounded">
                          {license.product?.thumbnail ? (
                            <img
                              src={url(license.product.thumbnail)}
                              alt={productName}
                              className="w-8 h-8 object-contain rounded"
                            />
                          ) : (
                            <Typography variant="info_12" className="text-gray_2">
                              {productName.charAt(0).toUpperCase()}
                            </Typography>
                          )}
                        </div>
                        
                        <div>
                          <button 
                            onClick={() => handleLicenseSelect(license._id)}
                            className="text-left hover:text-primary transition-colors cursor-pointer"
                          >
                            <Typography 
                              variant="body_16" 
                              className="font-medium text-text hover:text-primary"
                            >
                              {productName}
                            </Typography>
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                },
              },
              {
                id: "seats",
                title: texts?.seats_column || "Seats",
                headerClassName: "w-[20%] font-medium",
                render: (_, license) => {
                  const usagePercentage = calculateUsagePercentage(license.usedSeats || 0, license.seats)
                  return (
                    <div>
                      <Typography 
                        variant="body_14" 
                        className="font-medium leading-tight my-0"
                      >
                        {license.usedSeats || 0} / {license.seats}
                      </Typography>

                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray_3 rounded-full h-1">
                          <div 
                            className="bg-primary h-1 rounded-full transition-all duration-300"
                            style={{ width: `${usagePercentage}%` }}
                          />
                        </div>

                        <Typography 
                          variant="info_16" 
                          className="text-gray_2 leading-none my-0"
                        >
                          {usagePercentage}%
                        </Typography>
                      </div>
                    </div>
                  )
                },
              },
              {
                id: "status",
                title: texts?.status_column || "Status",
                headerClassName: "w-[20%] text-center font-medium",
                render: (_, license) => {
                  const statusConfig = getStatusConfig(license.status)
                  return (
                    <div className="text-center">
                      <span className={`font-medium rounded-full px-3 py-1  ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  )
                },
              },
              {
                id: "actions",
                title: texts?.actions_column || "Actions",
                headerClassName: "w-[20%] text-center font-medium",
                render: (_, license) => (
                  <Button
                    variant="secondary_s"
                    onClick={() => handleLicenseSelect(license._id)}
                    className="flex items-center gap-2 mx-auto"
                  >
                    <EyeIcon className="w-4 h-4" />
                    {texts?.details_button || "Details"}
                  </Button>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  )
}

export default Activate